import { PartnerDetailsType, PartnerRecords } from '~/campaigns/types/campaign.types'
import CoxLogo from '~/components/Icons/svgs/CoxLogo'
import appConfig from '~/config/app'
import { AppLanguage } from '~/types/locale'

export const enum OFFER_TYPE {
  PROCENTAGE_OFF = 'percentage_off',
  AMOUNT_OFF = 'amount_off',
  FREE_MONTHS = 'free_months',
}

// Standalone export, NOT a key in PartnerDetails: Accenture has its own static page, and getStaticPaths in
// pages/campaigns/[...slug].tsx would emit a colliding /campaigns/accenture — a fatal build error.
export const ACCENTURE_PARTNER_DETAILS: PartnerDetailsType = {
  name: 'Accenture',
  couponCode: appConfig.isProdEnvironment
    ? { monthly: 'LEizIUS2', yearly: 'LEizIUS2' }
    : { monthly: 'jDEiQ8Qg', yearly: 'jDEiQ8Qg' },
  offerType: OFFER_TYPE.PROCENTAGE_OFF,
  procentageOff: { monthly: 20, yearly: 20 },
  language: AppLanguage.NO,
}

export const PartnerDetails: PartnerRecords = {
  cox: {
    name: 'Cox Enterprises',
    showOfferBadge: true,
    landingHeroNoteKey: 'customText.coxHeroNote',
    couponCode: { monthly: '1LuKNy1E' },
    hideHeroTitle: false,
    onlyMonthlyOffer: true,
    offerType: OFFER_TYPE.FREE_MONTHS,
    freeMonths: 2,
    language: AppLanguage.EN,
    logo: {
      logoType: 'svgIcon',
      svgIcon: CoxLogo,
      logoSize: 150,
      className: 'max-h-12 max-w-25 md:max-w-max w-auto object-contain text-white',
      heroClassName: 'h-7 w-17 md:h-13 md:w-32 text-white',
    },
    // The customText.partner* keys are shared with Peachjar.
    customTextKey: {
      titleKey: 'customText.partnerTitle',
      titleClassName: 'font-extrabold',
      titleColorClassName: 'text-blue-pastel',
      descriptionKey: 'customText.partnerSubtitle',
      ctaKey: 'customText.partnerCta',
      ctaClassName: 'px-6 md:px-12 text-sm text-center',
      trustTextKey: 'customText.partnerTrust',
    },
  },
  appalachian: {
    name: 'Appalachian Wireless',
    showOfferBadge: true,
    couponCode: { monthly: '6XdACQPd' },
    hideHeroTitle: false,
    onlyMonthlyOffer: true,
    offerType: OFFER_TYPE.FREE_MONTHS,
    freeMonths: 2,
    language: AppLanguage.EN,
    logo: {
      logoType: 'awsImage',
      src: 'partners/appalachian-white.png',
      logoHeight: 80,
      logoWidth: 300,
      imageClassName: 'max-h-12 max-w-25 md:max-w-max w-auto object-contain',
    },
  },
  acom: {
    name: 'Alaska Communications',
    showOfferBadge: true,
    couponCode: { monthly: 'j9QLcf4T', yearly: 'j9QLcf4T' },
    hideHeroTitle: false,
    onlyMonthlyOffer: false,
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 25, yearly: 25 },
    language: AppLanguage.EN,
    logo: {
      logoType: 'awsImage',
      src: 'partners/acomLogoWhite.png',
      logoHeight: 100,
      logoWidth: 180,
      imageClassName: 'max-h-12 max-w-25 md:max-w-max w-auto object-contain',
    },
  },
  peachjar: {
    name: 'Peach Jar',
    couponCode: { monthly: 'peachjar30', yearly: 'peachjarb2s' },
    customPriceData: {
      yearlyPriceId: appConfig.isProdEnvironment ? 'price_1TzGgrJhgnSRU9kuSN7c51iO' : 'price_1TysmiJhgnSRU9kueZnkgZI1',
    },
    hideHeroTitle: false,
    onlyMonthlyOffer: false,
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 30, yearly: 44.44 },
    discountDuration: { yearly: 'firstYear' },
    // Required: no percentage anywhere for Peachjar, the plan screen's "Save x%" badge included.
    hideProcentageOff: true,
    language: AppLanguage.EN,
    keepsBackToSchool2026: true,
    hidePreTitle: true,
    // The pricing section is the price box and its CTA only; everything the offer used to say now lives in the hero.
    hidePricingText: true,
    logo: {
      logoType: 'awsImage',
      src: 'partners/peachjar-logo.png',
      logoHeight: 80,
      logoWidth: 200,
      imageClassName: 'max-h-8 -mt-1.5 max-w-25 md:max-w-max w-auto object-contain',
    },
    // No heroImage: falls through to the same default hero the Cox page uses (campaign/male-student.png).
    // The customText.partner* keys are shared with Cox.
    customTextKey: {
      preTitleKey: 'customText.peachJarPreTitle',
      titleKey: 'customText.partnerTitle',
      titleClassName: 'font-extrabold',
      titleColorClassName: 'text-blue-pastel',
      descriptionKey: 'customText.partnerSubtitle',
      ctaKey: 'customText.partnerCta',
      ctaClassName: 'px-6 md:px-12 text-sm text-center',
      trustTextKey: 'customText.partnerTrust',
    },
  },
  atxmom: {
    name: 'Austin Moms',
    showOfferBadge: true,
    couponCode: { monthly: 'MNok7s9b', yearly: 'MNok7s9b' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 20, yearly: 20 },
    language: AppLanguage.EN,
    logo: {
      logoType: 'awsImage',
      src: 'partners/Austin-Moms-Logo.png',
      logoHeight: 80,
      logoWidth: 80,
      heroClassName: 'md:scale-110',
      imageClassName: 'max-w-12 max-h-12 object-contain',
    },
  },
  elevkalenderen: {
    name: 'Elevkalenderen',
    showOfferBadge: true,
    couponCode: { monthly: '3QDJatG1', yearly: 'DiSOYqDs' },
    hideHeroTitle: false,
    onlyMonthlyOffer: false,
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 10, yearly: 25 },
    discountDuration: { yearly: 'firstYear' },
    language: AppLanguage.NO,
    logo: {
      logoType: 'awsImage',
      src: 'partners/elevkalender-logo-v2.png',
      logoHeight: 80,
      logoWidth: 120,
      imageClassName: 'min-w-full max-w-25 md:max-w-max md:max-h-12 object-contain -ml-2',
    },
    customTextKey: {
      heroParentTextClassName: 'md:-mr-25',
      // preTitleKey: 'customText.elevkalenderPreTitle',
      // preTitleClassName: 'tracking-normal',
      titleKey: 'customText.elevkalenderTitle',
      titleClassName: 'md:max-w-175',
      descriptionKey: 'customText.elevkalenderSubtitle',
      descriptionClassName: 'md:max-w-175',
    },
  },
  cfa: {
    name: 'Chick-Fil-A employees',
    showOfferBadge: true,
    couponCode: appConfig.isProdEnvironment
      ? { monthly: 'AgA9NOFQ', yearly: 'AgA9NOFQ' }
      : { monthly: 'gnYos6re', yearly: 'gnYos6re' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 20, yearly: 20 },
    language: AppLanguage.EN,
    heroImage: {
      src: 'partners/chick-fil-a-male-employee.png',
      width: 550,
      height: 550,
      className: 'object-contain',
    },
    logo: {
      logoType: 'awsImage',
      src: 'partners/chick-fil-a-logo.svg',
      logoHeight: 80,
      logoWidth: 120,
      className: 'w-full max-w-25 md:max-w-max h-full md:max-h-12 object-contain',
      imageClassName: 'h-full w-full object-contain',
    },
    customTextKey: {
      descriptionKey: 'customText.cfaDescription',
    },
  },
  cellcom: {
    name: 'Cellcom',
    showOfferBadge: true,
    couponCode: { monthly: 'TeQWn2gb', yearly: 'TeQWn2gb' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 20, yearly: 20 },
    language: AppLanguage.EN,
    logo: {
      logoType: 'awsImage',
      src: 'partners/cellcom-logo.svg',
      logoHeight: 80,
      logoWidth: 90,
      className: 'mt-1.5 w-full max-w-16 md:max-w-max h-full md:max-h-6 object-contain',
      imageClassName: 'h-full w-full object-contain',
    },
  },
  amazon: {
    name: 'Amazon',
    showOfferBadge: true,
    couponCode: { monthly: 'pyHL0H8B', yearly: 'pyHL0H8B' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 20, yearly: 20 },
    language: AppLanguage.EN,
    logo: {
      logoType: 'awsImage',
      src: 'partners/amazon-white.png',
      logoHeight: 80,
      logoWidth: 120,
      heroClassName: 'translate-y-1',
      className: 'mt-1.5 w-full max-w-16 md:max-w-max h-full md:max-h-8 object-contain',
      imageClassName: 'h-full w-full object-contain',
    },
    customTextKey: {
      descriptionKey: 'customText.amazonSubtitle',
      percentageOffKey: 'customText.amazon.percentageOffTxt',
    },
  },
  stackbyme: {
    name: 'Stack by me',
    showOfferBadge: true,
    couponCode: { monthly: 'ynI5TLF8', yearly: 'ynI5TLF8' },
    offerType: OFFER_TYPE.PROCENTAGE_OFF,
    procentageOff: { monthly: 20, yearly: 20 },
    logo: {
      logoType: 'awsImage',
      src: 'partners/stack-logo.png',
      logoHeight: 120,
      logoWidth: 200,
      className: 'mt-1.5 w-full max-w-16 md:max-w-max h-full md:max-h-10  object-contain',
      imageClassName: 'h-full w-full object-contain',
    },
  },
  dnb: {
    'ung-pluss': {
      name: 'DNB Ung Pluss',
      usesLandingHero: true,
      couponCode: { monthly: '', yearly: '' },
      offerType: OFFER_TYPE.PROCENTAGE_OFF,
      procentageOff: { monthly: 0, yearly: 0 },
      language: AppLanguage.NO,
      isLongPage: true,
      customPriceData: {
        monthlyPriceId: appConfig.isProdEnvironment
          ? 'price_1UM2mbJhgnSRU9kun2zrwvRk'
          : 'price_1Rh8c8JhgnSRU9kuX7Oj9BmJ',
        yearlyPriceId: appConfig.isProdEnvironment
          ? 'price_1UM2pCJhgnSRU9kuHTlHDwUZ'
          : 'price_1Rh8eLJhgnSRU9kuHHZMkKvO',
      },
      logo: {
        logoType: 'awsImage',
        src: 'partners/dnb-logo.svg',
        logoHeight: 40,
        logoWidth: 60,
        imageClassName: 'h-5 md:h-12 mt-1.5 md:mt-0 w-auto object-contain',
      },
      customTextKey: {
        offerTextKey: 'customText.dnbOffer',
      },
    },
    'saga-private-banking': {
      name: 'DNB Saga Private Banking',
      usesLandingHero: true,
      couponCode: { monthly: '', yearly: '' },
      offerType: OFFER_TYPE.PROCENTAGE_OFF,
      procentageOff: { monthly: 0, yearly: 0 },
      language: AppLanguage.NO,
      isLongPage: true,
      hasToD: true,
      customPriceData: {
        monthlyPriceId: appConfig.isProdEnvironment
          ? 'price_1UM2mbJhgnSRU9kun2zrwvRk'
          : 'price_1Rh8c8JhgnSRU9kuX7Oj9BmJ',
        yearlyPriceId: appConfig.isProdEnvironment
          ? 'price_1UM2pCJhgnSRU9kuHTlHDwUZ'
          : 'price_1Rh8eLJhgnSRU9kuHHZMkKvO',
      },
      logo: {
        logoType: 'awsImage',
        src: 'partners/dnb-logo.svg',
        logoHeight: 40,
        logoWidth: 60,
        imageClassName: 'h-5 md:h-12 mt-1.5 md:mt-0 w-auto object-contain',
      },
      customTextKey: {
        offerTextKey: 'customText.dnbOffer',
      },
    },
  },
} as const satisfies PartnerRecords
